import { FormControl, FormHelperText } from "@mui/material";
import { isValidPhoneNumber } from "libphonenumber-js/max";
import {
  MuiTelInput,
  MuiTelInputCountry,
  MuiTelInputInfo,
} from "mui-tel-input";
import { FC, useState } from "react";
import { Controller } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { IInput, IMuiFormControl, ValidationHelpers } from "@ryde/shared";

const DEFAULT_COUNTRY: MuiTelInputCountry = "IL";

export const PhoneInputByControl: FC<IMuiFormControl & IInput> = ({
  control,
  name,
  formLabel,
  formValidation,
  e2eData = "phone-input",
  size = "medium",
  sx,
  tabIndex,
}) => {
  const [country, setCountry] = useState<MuiTelInputCountry>(DEFAULT_COUNTRY);

  const { t, i18n } = useTranslation();

  const validateCustom = (value: string): boolean | string => {
    if (!value?.trim()) {
      return true;
    }

    return isValidPhoneNumber(value, country);
  };

  const getHandleChange =
    (onChange: (...event: unknown[]) => void) =>
    (value: string, info: MuiTelInputInfo) => {
      setCountry(info?.countryCode ?? DEFAULT_COUNTRY);
      onChange(info?.numberValue?.replace("+", "") || "");
    };

  const field = t("errors.requiredField", { fieldName: t("profile.phone") });
  return (
    <div dir={"ltr"}>
      <Controller
        name={name}
        control={control}
        rules={{
          required: ValidationHelpers.hasRequiredRule(formValidation) && field,
          validate: validateCustom,
        }}
        defaultValue=""
        render={({
          field: { ref, onChange, ...restField },
          fieldState: { error },
        }) => (
          <FormControl error={!!error}>
            <MuiTelInput
              inputRef={ref}
              {...restField}
              langOfCountryName={i18n.language}
              preferredCountries={[DEFAULT_COUNTRY]}
              defaultCountry={DEFAULT_COUNTRY}
              label={formLabel}
              required={ValidationHelpers.hasRequiredRule(formValidation)}
              error={!!error}
              data-e2e={e2eData}
              data-e2e-field-name={name}
              fullWidth
              onChange={getHandleChange(onChange)}
              size={size}
              sx={sx}
              tabIndex={tabIndex}
              MenuProps={{
                sx: {
                  ["& .MuiTelInput-Typography-calling-code"]: {
                    unicodeBidi: "plaintext",
                  },
                },
              }}
            />
            {error && (
              <FormHelperText>
                {/*TODO: add translation solution*/}
                {error.message || t("errors.invalidPhone")}
              </FormHelperText>
            )}
          </FormControl>
        )}
      />
    </div>
  );
};
